import {
  CalendarClock,
  Eye,
  Loader2,
  Mail,
  Moon,
  Pencil,
  Plus,
  Send,
  Sparkles,
  Sun,
  Sunset,
  Trash2
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import {
  FREQUENCY_LABEL,
  PERIOD_HINT,
  PERIOD_LABEL,
  Quota,
  Report,
  ReportFrequency,
  TimePeriod,
  WEEKDAYS,
  apiCreateReport,
  apiDeleteReport,
  apiLastEmail,
  apiListReports,
  apiPatchReport,
  apiPreviewHtml,
  apiQuota,
  apiSlotPreview,
  apiSuggestTitle,
  apiTestSend,
  formatLocalDateTime,
  formatTime,
  scheduleText
} from './api';

// Everything this dialog does, the chat can do too (register/list/update/
// delete_report) — both write the same notification_subscriptions_v2 rows.

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the create form with this question (the 🔔 make-it-recurring
   *  chip under chat answers) and open directly on the form view. */
  initialQuestion?: string;
}

interface FormState {
  title: string;
  question: string;
  frequency: ReportFrequency;
  period: TimePeriod;
  weekday: string;
  dayOfMonth: string;
  recipient: string;
  sendNow: boolean;
}

const EMPTY_FORM: FormState = {
  title: '',
  question: '',
  frequency: 'DAILY',
  period: 'MORNING',
  weekday: '0',
  dayOfMonth: '1',
  recipient: '',
  sendNow: true
};

const PERIOD_ICON: Record<TimePeriod, typeof Sun> = {
  MORNING: Sun,
  EVENING: Sunset,
  NIGHT: Moon
};

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

const clean = (s: string) => s.replace(/\s+/g, ' ').trim();

function statusBadge(r: Report) {
  if (!r.last_status) return null;
  const failed = r.last_status.startsWith('FAILED');
  const style = failed
    ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800'
    : r.last_status === 'SENT'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800'
      : 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800';
  const label =
    r.last_status === 'SENT' ? 'Sent ✓' : failed ? 'Failed' : r.last_status;
  return (
    <Badge variant="outline" className={`text-[11px] ${style}`}>
      {label}
    </Badge>
  );
}

// What the preview pane is currently showing.
type Preview =
  | { kind: 'sample'; html: string }
  | { kind: 'real'; html: string; reportTitle: string }
  | { kind: 'waiting'; reportId: number; reportTitle: string };

export default function ReportsDialog({
  open,
  onOpenChange,
  initialQuestion
}: Props) {
  const [reports, setReports] = useState<Report[]>([]);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editing, setEditing] = useState<Report | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [slot, setSlot] = useState<{ hour: number; minute: number } | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const sampleCache = useRef<string | null>(null);
  const pollWatermark = useRef<string | null>(null);
  const titleTouched = useRef(false);
  const recipientTouched = useRef(false);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([apiListReports(), apiQuota().catch(() => null)])
      .then(([list, q]) => {
        setReports(list.items);
        if (q) setQuota(q);
      })
      .catch((e) =>
        setNotice({ kind: 'err', text: `Could not load reports: ${e.message}` })
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    setNotice(null);
    setPreview(null);
    if (initialQuestion) {
      setEditing(null);
      titleTouched.current = false;
      recipientTouched.current = false;
      setForm({
        ...EMPTY_FORM,
        question: initialQuestion,
        title: clean(initialQuestion).slice(0, 60)
      });
      setView('form');
      // Replace the sliced fallback with a proper suggested title, unless the
      // user starts typing their own before it arrives.
      setSuggesting(true);
      apiSuggestTitle(initialQuestion).then((t) => {
        setSuggesting(false);
        if (t && !titleTouched.current) setForm((f) => ({ ...f, title: t }));
      });
    } else {
      setView('list');
    }
    refresh();
  }, [open, refresh, initialQuestion]);

  // Show the exact delivery time the chosen window resolves to.
  useEffect(() => {
    if (view !== 'form') return;
    let cancelled = false;
    setSlot(null);
    apiSlotPreview(form.period)
      .then((s) => {
        if (!cancelled) setSlot({ hour: s.hour_local, minute: s.minute_local });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [view, form.period]);

  // Poll for the real email after a test send: the worker computes the answer
  // within a minute or two, then the stored HTML of the email that was
  // ACTUALLY sent replaces the waiting card.
  useEffect(() => {
    if (!open || preview?.kind !== 'waiting') return;
    const { reportId, reportTitle } = preview;
    let tries = 0;
    const timer = window.setInterval(async () => {
      tries += 1;
      if (tries > 30) {
        window.clearInterval(timer);
        setNotice({
          kind: 'err',
          text: 'The report is taking longer than usual — check your inbox in a few minutes, or reopen this window later.'
        });
        setPreview(null);
        return;
      }
      try {
        const res = await apiLastEmail(reportId);
        if (res && res.sentAt !== pollWatermark.current) {
          window.clearInterval(timer);
          setPreview({ kind: 'real', html: res.html, reportTitle });
          setNotice({
            kind: 'ok',
            text: `“${reportTitle}” was just emailed — this is the exact email that went out.`
          });
          refresh();
        }
      } catch {
        /* keep polling */
      }
    }, 8000);
    return () => window.clearInterval(timer);
  }, [open, preview, refresh]);

  const showSample = useCallback(async () => {
    if (sampleCache.current) {
      setPreview({ kind: 'sample', html: sampleCache.current });
      return;
    }
    setPreview({ kind: 'sample', html: '' });
    try {
      const html = await apiPreviewHtml();
      sampleCache.current = html;
      setPreview({ kind: 'sample', html });
    } catch {
      setPreview(null);
      setNotice({ kind: 'err', text: 'Could not load the design preview.' });
    }
  }, []);

  const showLastEmail = async (r: Report) => {
    setBusyId(r.id);
    setNotice(null);
    try {
      const res = await apiLastEmail(r.id);
      if (res) {
        setPreview({ kind: 'real', html: res.html, reportTitle: r.title });
      } else {
        setNotice({
          kind: 'err',
          text: 'This report has not been emailed yet.'
        });
      }
    } catch (e: any) {
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const startCreate = () => {
    setEditing(null);
    titleTouched.current = false;
    recipientTouched.current = false;
    setForm(EMPTY_FORM);
    setNotice(null);
    setView('form');
  };

  const startEdit = (r: Report) => {
    setEditing(r);
    titleTouched.current = true;
    setForm({
      title: r.title,
      question: r.question,
      frequency: r.frequency,
      period: (r.time_period && r.time_period !== 'EXACT'
        ? r.time_period
        : 'MORNING') as TimePeriod,
      weekday: String(r.weekday ?? 0),
      dayOfMonth: String(r.day_of_month ?? 1),
      recipient: r.recipient_email,
      sendNow: false
    });
    setNotice(null);
    setView('form');
  };

  const suggestTitle = async () => {
    if (form.question.trim().length < 5 || suggesting) return;
    setSuggesting(true);
    const t = await apiSuggestTitle(form.question.trim());
    setSuggesting(false);
    if (t) {
      titleTouched.current = false;
      setForm((f) => ({ ...f, title: t }));
    }
  };

  // The address this user last sent a report to. A new report starts with it
  // instead of an empty box: on 2026-10-14 sobhy's project manager opened the
  // form four times in front of the owner and never registered a report,
  // because the button stays disabled until an address is typed and the box
  // sits at the bottom of the form.
  const lastRecipient = useMemo(() => {
    const sent = [...reports]
      .filter((r) => EMAIL_RE.test((r.recipient_email || '').trim()))
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return sent.length ? sent[0].recipient_email.trim() : '';
  }, [reports]);

  useEffect(() => {
    if (view !== 'form' || editing || recipientTouched.current) return;
    if (!lastRecipient || form.recipient) return;
    setForm((f) => (f.recipient ? f : { ...f, recipient: lastRecipient }));
  }, [view, editing, lastRecipient, form.recipient]);

  // What still keeps the button disabled, said next to it.
  const missing = useMemo(() => {
    const out: string[] = [];
    if (!clean(form.title)) out.push('a report title');
    if (form.question.trim().length < 5) out.push('the question');
    if (!EMAIL_RE.test(form.recipient.trim())) out.push('a valid recipient email');
    return out;
  }, [form]);
  const formValid = missing.length === 0;

  const submit = async () => {
    if (!formValid || saving) return;
    setSaving(true);
    setNotice(null);
    const common = {
      title: clean(form.title),
      question: form.question.trim(),
      frequency: form.frequency,
      time_period: form.period,
      weekday: form.frequency === 'WEEKLY' ? Number(form.weekday) : null,
      day_of_month:
        form.frequency === 'MONTHLY' ? Number(form.dayOfMonth) : null,
      recipient_email: form.recipient.trim()
    };
    try {
      if (editing) {
        await apiPatchReport(editing.id, common);
        setNotice({ kind: 'ok', text: `“${common.title}” updated.` });
        setView('list');
        refresh();
      } else {
        const created = await apiCreateReport({
          ...common,
          send_now: form.sendNow
        });
        setView('list');
        refresh();
        if (created.instant_copy_queued) {
          setNotice({
            kind: 'ok',
            text: `“${common.title}” registered — computing your test copy now. It will be emailed to ${common.recipient_email} and shown below shortly.`
          });
          pollWatermark.current = null;
          setPreview({
            kind: 'waiting',
            reportId: created.id,
            reportTitle: common.title
          });
        } else {
          setNotice({ kind: 'ok', text: `“${common.title}” registered.` });
        }
      }
    } catch (e: any) {
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r: Report) => {
    setBusyId(r.id);
    try {
      await apiPatchReport(r.id, { active: !r.active });
      refresh();
    } catch (e: any) {
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const testSend = async (r: Report) => {
    setBusyId(r.id);
    setNotice(null);
    try {
      pollWatermark.current = null;
      try {
        const existing = await apiLastEmail(r.id);
        pollWatermark.current = existing?.sentAt ?? null;
      } catch {
        /* no previous send */
      }
      setPreview({ kind: 'waiting', reportId: r.id, reportTitle: r.title });
      const res = await apiTestSend(r.id);
      setNotice({
        kind: 'ok',
        text: `Test email queued for ${res.recipient_email} — computing fresh numbers now, it will appear below shortly.`
      });
      refresh();
    } catch (e: any) {
      setPreview(null);
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: Report) => {
    if (!window.confirm(`Delete the report “${r.title}”?`)) return;
    setBusyId(r.id);
    try {
      await apiDeleteReport(r.id);
      setNotice({ kind: 'ok', text: `“${r.title}” deleted.` });
      refresh();
    } catch (e: any) {
      setNotice({ kind: 'err', text: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const atLimit = !!quota && quota.total_used >= quota.total_limit;
  // No daily slots left (or none at all on the free plan once the trial ends).
  const dailyLocked =
    !!quota &&
    quota.daily_used >= quota.daily_limit &&
    form.frequency !== 'DAILY';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        style={{ borderRadius: 12, borderColor: 'var(--sig-border)' }}
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2 text-[17px] font-semibold"
            style={{ color: 'var(--sig-ink)' }}
          >
            <CalendarClock
              className="size-5"
              style={{ color: 'var(--sig-blue)' }}
            />
            Scheduled Reports
          </DialogTitle>
          <DialogDescription
            className="text-[13px]"
            style={{ color: 'var(--sig-muted)' }}
          >
            Questions answered automatically and emailed on your schedule. The
            same reports can also be managed from the chat.
          </DialogDescription>
        </DialogHeader>

        {notice && (
          <div
            className={`rounded-lg border px-3 py-2 text-sm ${
              notice.kind === 'ok'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'
            }`}
          >
            {notice.text}
          </div>
        )}

        {view === 'list' ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={startCreate} disabled={atLimit}>
                  <Plus className="size-4 mr-1" /> New report
                </Button>
                {quota && (
                  <span className="text-xs text-muted-foreground">
                    {quota.total_used} / {quota.total_limit} reports
                    {quota.daily_limit > 0 && (
                      <>
                        {' '}
                        · {quota.daily_used} / {quota.daily_limit} daily
                      </>
                    )}
                    {quota.plan === 'FREE_TRIAL' && (
                      <> · daily trial: {quota.trial_days_left} days left</>
                    )}
                    {quota.plan === 'FREE' && <> · Free plan</>}
                  </span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={showSample}>
                <Eye className="size-4 mr-1" /> Email design
              </Button>
            </div>

            {atLimit && quota && (
              <div
                className="px-3 py-2 text-[13px]"
                style={{
                  border: '1px solid var(--sig-warn-border)',
                  background: 'var(--sig-warn-bg)',
                  color: 'var(--sig-warn-text)',
                  borderRadius: 11
                }}
              >
                You're using all {quota.total_limit} report slots.
                {quota.plan === 'FREE'
                  ? ` The Pro plan includes ${quota.pro_total_limit} reports (up to ${quota.pro_daily_limit} daily).`
                  : ' Delete one to free a slot.'}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="size-5 animate-spin mr-2" /> Loading…
              </div>
            ) : reports.length === 0 ? (
              <div className="rounded-xl border border-dashed p-8 text-center text-muted-foreground">
                <Mail className="size-8 mx-auto mb-3 opacity-50" />
                <div className="font-medium mb-1">No scheduled reports yet</div>
                <div className="text-sm">
                  Register a question you care about — like “net sales
                  yesterday” — and it lands in your inbox on schedule.
                </div>
              </div>
            ) : (
              reports.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-xl border p-4 flex flex-col gap-2 ${
                    r.active ? '' : 'opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">
                          {r.title}
                        </span>
                        {statusBadge(r)}
                        {!r.active && (
                          <Badge variant="outline" className="text-[11px]">
                            Paused
                          </Badge>
                        )}
                      </div>
                      <div
                        className="text-sm text-muted-foreground mt-1 line-clamp-2"
                        dir="auto"
                      >
                        {r.question}
                      </div>
                    </div>
                    <Switch
                      checked={r.active}
                      disabled={busyId === r.id}
                      onCheckedChange={() => toggleActive(r)}
                      aria-label="Active"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                    <span>🗓️ {scheduleText(r)}</span>
                    <span>📧 {r.recipient_email}</span>
                    {r.active && (
                      <span>⏭️ Next: {formatLocalDateTime(r.next_run_at)}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 justify-end flex-wrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === r.id}
                      onClick={() => showLastEmail(r)}
                    >
                      <Eye className="size-4 mr-1" /> Last email
                    </Button>
                    {!r.test_send_used && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busyId === r.id || !r.active}
                        onClick={() => testSend(r)}
                        title="One test email per report"
                      >
                        <Send className="size-4 mr-1" /> Send test email
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(r)}
                    >
                      <Pencil className="size-4 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      disabled={busyId === r.id}
                      onClick={() => remove(r)}
                    >
                      <Trash2 className="size-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Report title</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground"
                  disabled={suggesting || form.question.trim().length < 5}
                  onClick={suggestTitle}
                >
                  {suggesting ? (
                    <Loader2 className="size-3.5 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="size-3.5 mr-1" />
                  )}
                  Suggest
                </Button>
              </div>
              <Input
                value={form.title}
                maxLength={120}
                dir="auto"
                placeholder="e.g. Daily sales report"
                onChange={(e) => {
                  titleTouched.current = true;
                  setForm({ ...form, title: e.target.value });
                }}
              />
            </div>

            <div className="grid gap-2">
              <Label>Question</Label>
              <Textarea
                value={form.question}
                rows={3}
                dir="auto"
                placeholder="Write the full question without fixed dates — e.g. net sales yesterday for all branches with the top 5 branches"
                onChange={(e) => setForm({ ...form, question: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                The question is answered fresh on every send against your latest
                data — keep it relative (“yesterday”, “this month”), not a fixed
                date.
              </p>
            </div>

            <div className="grid gap-2">
              <Label>Delivery window</Label>
              <div className="grid grid-cols-3 gap-2">
                {(['MORNING', 'EVENING', 'NIGHT'] as TimePeriod[]).map((p) => {
                  const Icon = PERIOD_ICON[p];
                  const selected = form.period === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setForm({ ...form, period: p })}
                      className={`rounded-xl border p-3 text-left transition-colors ${
                        selected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <Icon className="size-4 mb-1.5 text-muted-foreground" />
                      <div className="text-sm font-medium">
                        {PERIOD_LABEL[p]}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {PERIOD_HINT[p]}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {slot ? (
                  <>
                    📬 Your report will be delivered at{' '}
                    <strong>{formatTime(slot.hour, slot.minute)}</strong> — the
                    quietest slot in this window, so it always arrives on time.
                  </>
                ) : (
                  'Finding the best delivery time…'
                )}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Frequency</Label>
                <Select
                  value={form.frequency}
                  onValueChange={(v) =>
                    setForm({ ...form, frequency: v as ReportFrequency })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['DAILY', 'WEEKLY', 'MONTHLY'] as ReportFrequency[]).map(
                      (f) => (
                        <SelectItem
                          key={f}
                          value={f}
                          disabled={f === 'DAILY' && dailyLocked}
                        >
                          {FREQUENCY_LABEL[f]}
                          {f === 'DAILY' && dailyLocked ? ' — Pro' : ''}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {dailyLocked && (
                  <p className="text-xs text-muted-foreground">
                    Daily delivery is a Pro feature.
                  </p>
                )}
              </div>

              {form.frequency === 'WEEKLY' && (
                <div className="grid gap-2">
                  <Label>Day of week</Label>
                  <Select
                    value={form.weekday}
                    onValueChange={(v) => setForm({ ...form, weekday: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.frequency === 'MONTHLY' && (
                <div className="grid gap-2">
                  <Label>Day of month</Label>
                  <Select
                    value={form.dayOfMonth}
                    onValueChange={(v) => setForm({ ...form, dayOfMonth: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label>Recipient email</Label>
              <Input
                dir="ltr"
                type="email"
                value={form.recipient}
                placeholder="you@company.com"
                onChange={(e) => {
                  recipientTouched.current = true;
                  setForm({ ...form, recipient: e.target.value });
                }}
              />
              <p className="text-xs text-muted-foreground">
                ⚠️ Make sure this is a real inbox — your app login email may be
                just a username, not a mailbox.
              </p>
            </div>

            {!editing && (
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Switch
                  checked={form.sendNow}
                  onCheckedChange={(v) => setForm({ ...form, sendNow: v })}
                />
                <span>
                  Email me a test copy now
                  <span className="block text-xs text-muted-foreground">
                    One test email per report.
                  </span>
                </span>
              </label>
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" onClick={showSample}>
                <Eye className="size-4 mr-1" /> Preview design
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setView('list')}
                  disabled={saving}
                >
                  Back
                </Button>
                <Button onClick={submit} disabled={!formValid || saving}>
                  {saving && <Loader2 className="size-4 animate-spin mr-1" />}
                  {editing ? 'Save changes' : 'Register report'}
                </Button>
              </div>
            </div>
            {!formValid && (
              <p className="text-xs text-amber-600 dark:text-amber-400 text-right">
                To register, add {missing.join(' and ')}.
              </p>
            )}
          </div>
        )}

        {preview && (
          <div className="mt-2 rounded-xl border overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-muted/50 text-sm gap-2">
              <span className="min-w-0 truncate">
                {preview.kind === 'sample' &&
                  '📧 Email design (sample data — real numbers are computed at send time)'}
                {preview.kind === 'real' &&
                  `📧 The actual last email of “${preview.reportTitle}” — exactly as delivered`}
                {preview.kind === 'waiting' &&
                  `⏳ Computing “${preview.reportTitle}” on your latest data…`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreview(null)}
              >
                Hide
              </Button>
            </div>
            {preview.kind === 'waiting' ||
            (preview.kind === 'sample' && !preview.html) ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                <Loader2 className="size-5 animate-spin mr-2" />
                {preview.kind === 'waiting'
                  ? 'Generating the answer and sending the email — usually under two minutes…'
                  : 'Loading…'}
              </div>
            ) : (
              <iframe
                title="email-preview"
                srcDoc={preview.html}
                sandbox=""
                className="w-full h-[480px] bg-white"
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
